<template>
    <div class="rtls-select-container" :class="cssClass">
        <p v-if="label" class="rtls-select-container__label">
            {{ toSentenceCase($t(label)) }}
        </p>
        <v-autocomplete
            v-bind="$attrs"
            ref="select"
            v-model="model"
            v-model:search="searchInput"
            class="rtls-select primary--text"
            :class="{
                'rtls-select--auto-height': (chips || multiple) && !horizontal,
                'rtls-select--white': !filled,
                'rtls-select--filed': filled,
            }"
            :items="displayOptions"
            :no-filter="prioritizeExactMatch && !!searchInput"
            :placeholder="toSentenceCase($t(placeholder || 'placeholders.select'))"
            :menu-props="{
                bottom: true,
                offsetY: true,
                openOnClick: false,
                contentClass,
                scrollStrategy: 'none',
                ...maxHeight,
            }"
            :density="density"
            :disabled="isDisabled"
            :multiple="multiple"
            :item-title="itemText"
            :item-value="itemValue"
            :small-chips="chips"
            :closable-chips="deletableChips"
            single-line
            :rules="isDisabled ? [] : rules"
            :validate-on="validateOnBlur ? 'blur' : undefined"
            :clearable="showClearButton"
            :return-object="returnObject"
            :menu-icon="appendIcon"
            :prepend-inner-icon="prependInnerIcon"
            :append-inner-icon="appendInnerIcon"
            :name="name"
            :max-width="maxWidth"
            :width="width"
            :bg-color="bgColor"
            :hide-details="hideDetails"
            :autocomplete="normalisedAutocomplete"
            @update:model-value="change"
            @change="change"
            @click:clear="clear"
            @click="handleClick"
            @blur="cleanNewlyChangedItems()"
            @keypress.enter.prevent
        >
            <template v-if="multiple && hasSelectAll" v-slot:prepend-item>
                <v-list-item ripple @click="toggle">
                    <v-list-item-action>
                        <v-icon :color="iconColor">{{ icon }}</v-icon>
                    </v-list-item-action>
                    <v-list-item-title>
                        {{ toSentenceCase(selectText) }}
                    </v-list-item-title>
                </v-list-item>
                <v-divider class="mt-2" />
            </template>

            <template v-if="chips" v-slot:chip="{ props: chipProps, item, index }">
                <v-tooltip
                    v-if="maxItemsToDisplaySelected || index < maxItemsInList - 1 || !finiteList"
                    right
                    content-class="rtls-tooltip"
                    :disabled="!item.invalid"
                >
                    <template v-slot:activator="{ props: tooltipProps }">
                        <v-chip
                            v-bind="{
                                ...chipProps,
                                ...tooltipProps,
                                ...(item.invalid ? { color: 'red', outlined: true } : {}),
                            }"
                            class="v-chip--select"
                            small
                            :closable="deletableChips && !isDisabled && !itemDisabled(item)"
                            :disabled="itemDisabled(item)"
                        >
                            <v-tooltip :disabled="hideTooltip" top>
                                <template v-slot:activator="{ props: innerTooltipProps }">
                                    <span
                                        v-bind="innerTooltipProps"
                                        ref="truncated"
                                        class="truncate-text"
                                        @mouseenter="checkTooltipRequired"
                                    >
                                        {{ getItemString(item) }}
                                    </span>
                                </template>
                                <span>{{ getItemString(item) }}</span>
                            </v-tooltip>
                        </v-chip>
                    </template>
                    {{ toSentenceCase($tkey(invalidSelectionText)) }}
                </v-tooltip>
                <span
                    v-if="!maxItemsToDisplaySelected && index === maxItemsInList - 1 && finiteList"
                >
                    <v-chip :closable="false" class="v-chip--select pr-2" small>
                        <span class="truncate-text">
                            {{ $t('planning.createCampaign.xMore', { number: xOthers }) }}
                            <v-menu bottom offset-y open-on-hover>
                                <template v-slot:activator="{ props: menuProps }">
                                    <v-icon
                                        v-if="!isDisabled"
                                        v-bind="menuProps"
                                        id="mdi"
                                        size="20"
                                        class="pl-1"
                                        color="primary"
                                        >mdi-dots-horizontal-circle</v-icon
                                    >
                                </template>
                                <v-list>
                                    <v-list-item
                                        v-for="(otherOption, othersIndex) in xOthersArray"
                                        :key="othersIndex"
                                        :disabled="itemDisabled(otherOption)"
                                    >
                                        <v-list-item-title class="vuex-select-tooltip-title">
                                            {{ otherOption.optionText }}</v-list-item-title
                                        >
                                        <span v-if="deletableChips">
                                            <v-icon
                                                :disabled="itemDisabled(otherOption)"
                                                :color="iconColor"
                                                @click.stop.prevent="deselectOne(otherOption.value)"
                                            >
                                                mdi-close-circle
                                            </v-icon>
                                        </span>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </span>
                    </v-chip>
                </span>
            </template>

            <template v-else v-slot:chip="{ item }">
                <v-tooltip :disabled="hideTooltip" top>
                    {{ getItemString(item) }}
                    <template v-slot:activator="{ props }">
                        <span
                            v-bind="props"
                            ref="truncated"
                            class="truncate-text"
                            @mouseenter="checkTooltipRequired"
                        >
                            {{ getItemString(item) }}
                        </span>
                    </template>
                    <span>{{ getItemString(item) }}</span>
                </v-tooltip>
            </template>

            <template v-slot:item="{ props }">
                <v-divider v-if="props.value && props.value.divider" />
                <v-list-item
                    v-else
                    v-bind="props"
                    density="compact"
                    :disabled="itemDisabled(props)"
                    :class="itemColourClass(props)"
                    @change="changeOne(props)"
                >
                    <template v-if="multiple" v-slot:prepend="{ isSelected, select }">
                        <v-list-item-action start>
                            <v-checkbox
                                class="rtls-checkbox"
                                color="primary"
                                :model-value="isSelected"
                                @update:model-value="select"
                            />
                        </v-list-item-action>
                    </template>

                    <template v-slot:title>
                        <v-row no-gutters>
                            <span>{{ text(props) }}</span>
                            <v-spacer />
                        </v-row>
                    </template>
                </v-list-item>
            </template>
        </v-autocomplete>
    </div>
</template>

<script>
import {
    sortBy,
    get,
    intersectionBy,
    intersection,
    size,
    isObject,
    isFunction,
    lowerCase,
    map,
    isArray,
} from 'lodash';
import vuexComponentMixin from '@/js/mixins/vuex-component';
import { nextTick } from 'vue';
import readonly from '@/js/mixins/readonly';
import useForceValidateOnMount from '@/js/composables/useForceValidateOnMount';

const sortByItemText = function(options) {
    return sortBy(options, [i => lowerCase(i[this.itemText])]);
};

export default {
    mixins: [vuexComponentMixin, readonly],
    inheritAttrs: false,

    props: {
        multiple: Boolean,
        options: [Array, Object],
        itemValue: {
            type: [String, Function],
            default: 'value',
        },
        itemText: {
            type: [String, Function],
            default: 'text',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        checkDisabled: {
            type: Function,
            default: () => false,
        },
        placeholder: {
            type: String,
        },
        label: String,
        cssClass: String,
        chips: Boolean,
        horizontal: Boolean,
        deletableChips: Boolean,
        filled: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        sortFunction: {
            type: Function,
            default: sortByItemText,
        },
        sortResults: {
            type: Boolean,
            default: true,
        },
        invalidSelectionText: {
            type: String,
            default: 'general.info.invalidSelection',
        },
        returnObject: {
            type: Boolean,
            default: false,
        },
        validateOnBlur: {
            type: Boolean,
            default: true,
        },
        finiteList: {
            type: Boolean,
            default: false,
        },
        maxItemsInList: {
            type: Number,
            default: 3,
        },
        hasSelectAll: {
            type: Boolean,
            default: true,
        },
        hideDropdown: {
            type: Boolean,
            default: false,
        },
        appendIcon: {
            type: String,
            default: 'custom:expand_more',
        },
        prependInnerIcon: {
            type: String,
            default: undefined,
        },
        appendInnerIcon: {
            type: String,
            default: undefined,
        },
        changeHandler: {
            type: Function,
            required: false,
        },
        forceValidateOnMount: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String,
            default: null,
        },
        maxWidth: {
            type: [String, Number],
            default: null,
        },
        width: {
            type: [String, Number],
            default: null,
        },
        bgColor: {
            type: String,
            default: null,
        },
        density: {
            type: String,
            default: 'default',
        },
        hideDetails: {
            type: [Boolean, String],
            default: null,
        },
        prioritizeExactMatch: {
            type: Boolean,
            default: false,
        },
        autocomplete: {
            type: [String, Boolean],
            default: false,
        },
    },
    emits: ['clicked', 'change'],
    setup() {
        useForceValidateOnMount({ refName: 'select' });
    },
    data() {
        return {
            searchInput: null,
            hideTooltip: null,
            newlyChanged: new Set(),
        };
    },

    computed: {
        contentClass() {
            let className = 'rtls-select__menu';
            if (this.chips || this.multiple) {
                className += ' rtls-select__menu--multiple';
            }

            return className;
        },
        getOptions() {
            const options = this.parsedField ? this.parsedField.options : this.options;
            return this.sortResults ? this.sortFunction(options) : options;
        },
        /**
         * Options to display in the dropdown.
         * When prioritizeExactMatch is enabled and user is searching, filters and
         * reorders results: exact matches first, then starts-with, then contains.
         * Otherwise returns getOptions unchanged.
         */
        displayOptions() {
            if (!this.prioritizeExactMatch || !this.searchInput) return this.getOptions;
            const search = this.searchInput.toLowerCase();
            const exact = [];
            const startsWith = [];
            const contains = [];
            this.getOptions.forEach(option => {
                const label = String(
                    isFunction(this.itemText)
                        ? this.itemText(option)
                        : get(option, this.itemText, option)
                ).toLowerCase();
                if (label === search) {
                    exact.push(option);
                } else if (label.startsWith(search)) {
                    startsWith.push(option);
                } else if (label.includes(search)) {
                    contains.push(option);
                }
            });
            return [...exact, ...startsWith, ...contains];
        },
        /**
         * All options (including disabled) that match the current search input.
         * When no search is active this equals displayOptions.
         */
        filteredVisibleOptions() {
            if (!this.searchInput) return this.displayOptions;
            if (this.prioritizeExactMatch) return this.displayOptions;
            const search = this.searchInput.toLowerCase();
            return this.displayOptions.filter(option => {
                const label = isFunction(this.itemText)
                    ? this.itemText(option)
                    : get(option, this.itemText, option);
                return String(label)
                    .toLowerCase()
                    .includes(search);
            });
        },
        /** Non-disabled options that are visible after filtering. */
        selectableFilteredOptions() {
            return this.filteredVisibleOptions.filter(option => !get(option, 'disabled', false));
        },
        /** Set of model values for O(1) membership checks. */
        modelValueSet() {
            if (!this.model || !isArray(this.model)) return new Set();
            return new Set(
                this.model.map(item => (this.returnObject ? item[this.itemValue] : item))
            );
        },
        selectAll() {
            if (this.modelValueSet.size === 0) return false;
            if (this.selectableFilteredOptions.length === 0) return false;
            return this.selectableFilteredOptions.every(option =>
                this.modelValueSet.has(option[this.itemValue])
            );
        },
        selectSome() {
            if (this.modelValueSet.size === 0) return false;
            if (this.selectAll) return false;
            return this.selectableFilteredOptions.some(option =>
                this.modelValueSet.has(option[this.itemValue])
            );
        },
        icon() {
            if (this.selectAll) return 'mdi-close-box';
            if (this.selectSome) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
        iconColor() {
            return this.model && this.model.length > 0 ? 'primary' : '';
        },
        isDisabled() {
            return this.checkDisabled() || this.disabled || this.isReadOnly;
        },
        selectText() {
            return this.selectAll ? this.$t('actions.deselectAll') : this.$t('actions.selectAll');
        },
        xOthers() {
            return size(this.model) - (this.maxItemsInList - 1);
        },
        xOthersArray() {
            const modelOptionsNotOnDisplayText = this.model
                .slice(this.maxItemsInList - 1)
                .map(modelItem => {
                    if (isFunction(this.itemText)) {
                        const optionText = this.itemText(modelItem);
                        const value = isFunction(this.itemValue)
                            ? this.itemValue(modelItem)
                            : modelItem;
                        return { value, optionText };
                    }
                    if (isObject(modelItem)) {
                        return {
                            value: modelItem[this.itemValue],
                            optionText: modelItem[this.itemText],
                        };
                    }
                    const item = this.getOptions.find(
                        option => option[this.itemValue] === modelItem
                    );
                    return { value: item[this.itemValue], optionText: item[this.itemText] };
                });
            return modelOptionsNotOnDisplayText;
        },
        disabledOptions() {
            return this.getOptions.filter(option => get(option, 'disabled', false));
        },
        disabledOptionsMap() {
            const optionsMap = new Map();
            this.getOptions.forEach(option => {
                optionsMap.set(get(option, this.itemValue), get(option, 'disabled', false));
            });
            return optionsMap;
        },
        showClearButton() {
            // We need to hide clear button when clearing deselects something user doesn't have access to.
            if (this.clearable) {
                // For the multiple case it is handled elsewise.
                if (this.multiple) return true;
                // Check if selected option is disabled
                // If returnObject === true we simply check model.
                if (this.returnObject) {
                    return !get(this.model, 'disabled', false);
                }
                // Else we find selected in options
                const itemValueIsFunction = isFunction(this.itemValue);
                const selectedOption = this.getOptions.find(option => {
                    if (itemValueIsFunction) {
                        return this.itemValue(option) === this.itemValue(this.model);
                    }
                    return option[this.itemValue] === this.model;
                });
                return !get(selectedOption, 'disabled', false);
            }
            return false;
        },
        maxItemsToDisplaySelected() {
            return size(this.model) === this.maxItemsInList;
        },
        maxHeight() {
            if (this.hideDropdown) {
                return { maxHeight: 0 };
            }
            return {};
        },
        normalisedAutocomplete() {
            if (this.autocomplete === true) {
                return 'on';
            }
            if (this.autocomplete === false) {
                return 'off';
            }
            return this.autocomplete;
        },
    },
    methods: {
        getItemString(item) {
            if (isObject(item)) {
                return isObject(item[this.itemText]) || !item[this.itemText]
                    ? item.title
                    : item[this.itemText];
            }
            return item;
        },
        handleClick(event) {
            if (this.hideDropdown) {
                event.stopPropagation();
            }
            this.$emit('clicked');
        },
        checkTooltipRequired(event) {
            // scrollWidth includes content not visible due to overflow, whilst offsetWidth includes only visible content.
            // If these differ, then a tooltip is required to display the full text.
            const { offsetWidth, scrollWidth } = event.target;
            this.hideTooltip = offsetWidth === scrollWidth;
        },

        deselectOne(value) {
            const model = isArray(this.model) ? this.model : [];
            this.model = model.filter(modelItem => {
                if (this.returnObject) {
                    const modelValue = isFunction(this.itemValue)
                        ? this.itemValue(modelItem)
                        : get(modelItem, this.itemValue);
                    return modelValue !== value;
                }
                return modelItem !== value;
            });
            this.change(this.model);
        },

        async toggle() {
            // Replaced directly changing model with calling setValue method from v-select
            // It helps with validation issue
            // when we do this.model = [] validation state change only in next tick and we send wrong request
            // or not sending correct request in case if user click 'select all' after clicking 'deselect all'
            // this.$emit('change', value) calling as part of setValue();
            await nextTick();

            const currentModel = this.model || [];

            if (this.selectAll) {
                // Deselect all selectable filtered options,
                // preserving disabled and outside-filter selections
                const selectableValues = new Set(
                    this.selectableFilteredOptions.map(o => o[this.itemValue])
                );
                const newModel = currentModel.filter(item => {
                    const itemValue = this.returnObject ? item[this.itemValue] : item;
                    return !selectableValues.has(itemValue);
                });
                this.model = newModel;
                this.change(this.model);
            } else {
                // Select all selectable filtered options,
                // preserving current selections outside the filtered set and disabled selections
                const selectableValues = new Set(
                    this.selectableFilteredOptions.map(ao => ao[this.itemValue])
                );

                // Keep currently selected items that are not selectable filtered options
                // (preserves disabled selections and selections outside the filter)
                const preservedSelection = currentModel.filter(item => {
                    const itemValue = this.returnObject ? item[this.itemValue] : item;
                    return !selectableValues.has(itemValue);
                });

                const newFilteredSelection = this.selectableFilteredOptions.map(option =>
                    this.returnObject ? option : option[this.itemValue]
                );

                this.model = [...preservedSelection, ...newFilteredSelection];
                this.change(this.model);
            }
        },

        change(value) {
            this.$emit('change', value);
            if (this.changeHandler) {
                this.changeHandler(value);
            }
        },

        changeOne(item) {
            const value = item.value;
            this.newlyChanged.add(value);
        },

        cleanNewlyChangedItems() {
            this.newlyChanged = new Set();
        },

        async reset() {
            // nextTick needed otherwise the model doesn't get updated
            await nextTick();
            this.model = null;
        },

        itemDisabled(item) {
            const key =
                get(item, `raw.${this.itemValue}`) || get(item, this.itemValue) || item.value;
            return this.disabledOptionsMap.get(key) || false;
        },

        itemColourClass(item) {
            if (!this.model) return 'unselected';
            if (this.newlyChanged.has(item.value)) return 'selected-black';
            if (isArray(this.model)) {
                return this.model.includes(item.value) ? 'selected-grey' : 'unselected';
            }
            return this.model === item.value ? 'selected-grey' : 'unselected';
        },

        text(item) {
            const value = item.value || item;
            if (typeof this.itemText === 'function') return this.itemText(value);
            let itemText = null;
            if (item.title) {
                itemText = 'title';
            }
            return get(item, itemText || this.itemText, value);
        },

        clear() {
            if (this.multiple) {
                const originalModel = this.model.slice();
                this.$nextTick(() => {
                    this.deselectAll(originalModel);
                });
            }
        },

        deselectAll(originalModel) {
            const disabledModel = this.returnObject
                ? intersectionBy(originalModel, this.disabledOptions, this.itemValue)
                : intersection(originalModel, map(this.disabledOptions, this.itemValue));
            // disabled options can't be deselected by "Deselect All" or "clear" buttons
            this.model = disabledModel;
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
.rtls-select-container {
    .rtls-select {
        &:deep() {
            .v-input__control .v-field__input input {
                padding-left: 0.5rem;
                &::placeholder {
                    color: $promo-grey-dark;
                    opacity: 1 !important;
                }
            }

            .v-input__control
                .v-input__slot
                .v-select__slot
                .v-select__selections
                span.truncate-text {
                padding-left: 0.5rem;
            }

            span.truncate-text {
                font-size: 1.2rem;
            }
        }
    }

    :deep(.v-input--is-disabled .v-theme--light.v-icon) {
        opacity: 0.3;
    }
}
:deep() {
    .v-menu__content {
        border-radius: 0 !important;
    }

    .mdi-close-circle {
        margin-left: 1rem;
        &:hover {
            color: $promo-black !important;
        }
    }
    #mdi:hover {
        color: $rtls-secondary-button-hover-colour !important;
    }

    .v-list-item--disabled > .v-list-item__content {
        color: $promo-grey-7 !important;
    }

    .v-list-item__title {
        font-size: 1.2rem;
    }

    .v-field--disabled {
        .v-field__append-inner {
            opacity: 0;
        }
    }

    .v-field__append-inner,
    .v-field__prepend-inner {
        .v-icon.v-autocomplete__menu-icon {
            font-size: 1.3rem !important;
            opacity: 1;
            use {
                fill: $promo-primary-colour !important;
            }
        }
    }

    .v-selection-control__wrapper,
    .v-selection-control__input {
        width: auto;
        height: auto;
    }

    .v-checkbox.rtls-checkbox * i.v-icon {
        font-size: 2.4rem;
    }

    .v-checkbox.rtls-checkbox .mdi-checkbox-blank-outline {
        color: rgba(0, 0, 0, 0.54) !important;
    }

    .v-chip.v-chip--size-small .v-chip__close {
        margin-inline-start: 0;
        margin-inline-end: 0;
    }

    .v-chip__close {
        .mdi-close-circle {
            font-size: 1.8rem;
            color: $promo-primary-colour !important;
            margin-left: 0.3rem;
        }
    }

    .v-field__clearable {
        opacity: 1;
    }

    .v-field__clearable {
        .mdi-close-circle {
            &::before {
                content: '\F156';
            }
        }
    }
}

// override rtls-ui-components.css property to place title and button in one row
.v-list-item-title {
    font-size: 1.2rem;
    padding-left: 0.3rem;
}

.rtls-select__menu--multiple {
    .v-icon.v-icon {
        font-size: 2.4rem;
    }

    .v-list {
        display: flex;
    }

    .v-list-item {
        flex: 1 1 100%;
        position: relative;
        text-decoration: none;

        &:first-of-type {
            height: 58px !important;
            padding-top: 13px !important;
            padding-bottom: 9px !important;
        }
    }
    .v-list-item__content {
        justify-content: left;
        min-height: 48px !important;
    }

    .v-list-item-action {
        .v-input {
            padding-left: 1.4rem;
            padding-top: 1.5rem;
        }
    }
}
</style>
